
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * ImportSource
 *
 * A configured third-party product from which a firm's existing data can be migrated into Outlaw Practice (e.g. Bill4Time, Pipedrive). Stores the product name and the firm's API key for that product; runtime state such as fetched documents, mapped output, and API-key validity are carried as transient fields during the import process.
 */
class ImportSource extends Base implements EntityType {
    importSourceId: uuid;
    name?: string | null;
    useFiles: boolean;
    apiKey?: string | null;
    apiKeyValid: boolean;
    fullDocuments: any[];
    outputMap?: any | null;
    docLink?: string | null;
    uses: string[];
    website?: string | null;

    /**
     * Create a new ImportSource, with an (optional) JSON object or ImportSource class
     *
     * @param {Object|ImportSource} doc - Can be either a simple JSON object or a ImportSource class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof ImportSource | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            importSourceId: genUUID(),
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'importSource';
        // @ts-ignore
        this.importSourceId = doc._importSourceId || doc.importSourceId || doc.importsourceid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.useFiles = util.defaultBoolean([doc.useFiles, doc._useFiles, doc.usefiles, false]);
        // @ts-ignore
        this.apiKey = doc._apiKey || doc.apiKey || doc.apikey || null;
        // @ts-ignore
        this.apiKeyValid = util.defaultBoolean([doc.apiKeyValid, doc._apiKeyValid, doc.apikeyvalid, false]);
        // @ts-ignore
        this.fullDocuments = doc._fullDocuments || doc.fullDocuments || doc.fulldocuments || [];
        // @ts-ignore
        this.outputMap = doc._outputMap || doc.outputMap || doc.outputmap || {};
        // @ts-ignore
        this.docLink = doc._docLink || doc.docLink || doc.doclink || null;
        // @ts-ignore
        this.uses = doc._uses || doc.uses || [];
        // @ts-ignore
        this.website = doc._website || doc.website || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the importSourceId across all entity types
     */
    get id(): uuid {
        return this.importSourceId;
    }


    /**
     * Add a fullDocument to importSource.fullDocuments
     *
     * @param {any} fullDocument - The FullDocument to add
     * @returns any - The fullDocument with any defaulted fields set
     */
    addFullDocument(fullDocument: any) : any {
        if (!fullDocument) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.fullDocuments) this.fullDocuments = [];
        this.fullDocuments.push(fullDocument);

        return fullDocument;
    }

    /**
     * Update a fullDocument from importSource
     *
     * @param {any} fullDocument - The FullDocument to remove
     */
    updateFullDocument(fullDocument: any) {
        if (!this.fullDocuments) this.fullDocuments = [];
        
        // Find the matching object by fullDocument.fullDocumentId
        const index = this.fullDocuments.findIndex(item => item === fullDocument);
        if (index !== -1) {
            this.fullDocuments[index] = fullDocument;
        }
        else this.addFullDocument(fullDocument);
    }

    /**
     * Remove a fullDocument from importSource
     *
     * @param {any} fullDocument - The FullDocument to remove
     */
    removeFullDocument(fullDocument: any) {
        if (!this.fullDocuments) return;
        
        // Find the matching object by fullDocument.fullDocumentId
        let foundItems = this.fullDocuments?.filter(item => item === fullDocument);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.fullDocuments = this.fullDocuments.filter(item => !(item === fullDocument));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonFullDocuments() {
        this.fullDocuments = [];
     }
        
    /**
     * Add a us to importSource.uses
     *
     * @param {string} us - The Us to add
     * @returns string - The us with any defaulted fields set
     */
    addUs(us: string) : string {
        if (!us) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.uses) this.uses = [];
        this.uses.push(us);

        return us;
    }

    /**
     * Update a us from importSource
     *
     * @param {string} us - The Us to remove
     */
    updateUs(us: string) {
        if (!this.uses) this.uses = [];
        
        // Find the matching object by us.usId
        const index = this.uses.findIndex(item => item === us);
        if (index !== -1) {
            this.uses[index] = us;
        }
        else this.addUs(us);
    }

    /**
     * Remove a us from importSource
     *
     * @param {string} us - The Us to remove
     */
    removeUs(us: string) {
        if (!this.uses) return;
        
        // Find the matching object by us.usId
        let foundItems = this.uses?.filter(item => item === us);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.uses = this.uses.filter(item => !(item === us));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonUses() {
        this.uses = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this import/export"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.importSourceId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["fullDocuments", "uses"];
    }

    /**
     * Default entries are Outlaw-supplied ImportSource entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): ImportSource[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'importSource',
            table: 'IMPORT_SOURCE',
            comment: `A configured third-party product from which a firm's existing data can be migrated into Outlaw Practice (e.g. Bill4Time, Pipedrive). Stores the product name and the firm's API key for that product; runtime state such as fetched documents, mapped output, and API-key validity are carried as transient fields during the import process.`,
            fields: {
                importSourceId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this import/export'
                },
                name: {
                    type: 'string',
                    comment: 'Name of the product being imported'
                },
                useFiles: {
                    type: 'boolean',
                    comment: 'Does this source use files?'
                },
                apiKey: {
                    type: 'string',
                    comment: 'If using an API, this is the customer key'
                },
                /**
                 * Transient: Is the api key valid?
                 */
                apiKeyValid: {
                    type: 'boolean',
                    transient: true,
                    comment: 'Is the key valid?'
                },
                /**
                 * Transient: Full document info, not just ids, with all document text in plainText
                 */
                fullDocuments: {
                    type: 'json[]',
                    transient: true,
                    comment: 'The full documents (required for the hook)'
                },
                /**
                 * Transient: The import results, mapped with string => object[]
                 */
                outputMap: {
                    type: 'json',
                    transient: true,
                    comment: 'Imported results (mapped from input sources to output schema)'
                },
                /**
                 * Transient: A URL with instructions to export files, or to get an API key
                 */
                docLink: {
                    type: 'string',
                    transient: true,
                    comment: 'Documentation link(s) for this import source'
                },
                /**
                 * Transient: what access methods does this source support?
                 * Valid values are apiKey and files.
                 */
                uses: {
                    type: 'string[]',
                    transient: true,
                    comment: 'The access methods this source supports (e.g. API key, file upload, OAuth)'
                },
                /**
                 * Transient: the full api of the product.
                 */
                website: {
                    type: 'string',
                    transient: true,
                    comment: 'URL for the product (landing page)'
                }
            }
        });
    }

}
export default ImportSource;
